/**
 * Componente para exibir filtros ativos de drill-down.
 * Mostra badges clicáveis para cada filtro ativo, permitindo remoção individual ou limpar todos.
 * Suporta multi-valores: cada valor aparece como badge individual removível.
 */

import React from "react";
import { X, Filter, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  useDrilldownOptional,
  DrillDimension,
  DrillFilter,
} from "./drilldown.context";

/**
 * Tradução de dimensões para português
 */
const dimensionLabels: Record<DrillDimension, string> = {
  channel_group: "Grupo de Canal",
  source_medium: "Origem/Mídia",
  session_source: "Origem da sessão",
  session_medium: "Meio da sessão",
  campaign: "Campanha",
  ad_content: "Conteúdo do anúncio",
  region: "Estado/Região",
  city: "Cidade",
  category: "Categoria",
  product: "Produto",
  device: "Dispositivo",
  search_term: "Pesquisa",
  date: "Data",
  new_returning: "Novo / Recorrente",
  landing_page: "Página de Destino",
  gender: "Gênero",
  age: "Idade",
  ad_platform: "Plataforma de anúncio",
};

/**
 * Cores para cada tipo de dimensão
 */
const dimensionColors: Record<DrillDimension, string> = {
  channel_group: "bg-blue-500/20 text-blue-300 hover:bg-blue-500/30",
  source_medium: "bg-green-500/20 text-green-300 hover:bg-green-500/30",
  session_source: "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30",
  session_medium: "bg-lime-500/20 text-lime-300 hover:bg-lime-500/30",
  campaign: "bg-purple-500/20 text-purple-300 hover:bg-purple-500/30",
  ad_content: "bg-fuchsia-500/20 text-fuchsia-300 hover:bg-fuchsia-500/30",
  region: "bg-yellow-500/20 text-yellow-300 hover:bg-yellow-500/30",
  city: "bg-orange-500/20 text-orange-300 hover:bg-orange-500/30",
  category: "bg-pink-500/20 text-pink-300 hover:bg-pink-500/30",
  product: "bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30",
  device: "bg-indigo-500/20 text-indigo-300 hover:bg-indigo-500/30",
  search_term: "bg-teal-500/20 text-teal-300 hover:bg-teal-500/30",
  date: "bg-gray-500/20 text-gray-300 hover:bg-gray-500/30",
  new_returning: "bg-amber-500/20 text-amber-300 hover:bg-amber-500/30",
  landing_page: "bg-sky-500/20 text-sky-300 hover:bg-sky-500/30",
  gender: "bg-violet-500/20 text-violet-300 hover:bg-violet-500/30",
  age: "bg-stone-500/20 text-stone-300 hover:bg-stone-500/30",
  ad_platform: "bg-indigo-500/20 text-indigo-300 hover:bg-indigo-500/30",
};

interface ActiveFiltersDisplayProps {
  /** Classe CSS adicional */
  className?: string;
  /** Mostrar ícone de filtro? */
  showIcon?: boolean;
  /** Texto quando não há filtros */
  emptyText?: string;
}

/**
 * Exibe os filtros ativos do contexto de drill-down.
 *
 * @example
 * ```tsx
 * <ActiveFiltersDisplay />
 * ```
 */
export function ActiveFiltersDisplay({
  className = "",
  showIcon = true,
  emptyText,
}: ActiveFiltersDisplayProps): React.ReactElement | null {
  const drilldown = useDrilldownOptional();

  // Se não estiver em contexto de drilldown, não renderiza nada
  if (!drilldown) {
    return null;
  }

  const {
    filters,
    clearFilter,
    clearFilterValue,
    clearAllFilters,
    breadcrumb,
  } = drilldown;
  const activeFilters = Object.values(filters) as DrillFilter[];

  // Se não há filtros ativos, pode mostrar texto vazio ou nada
  if (activeFilters.length === 0) {
    if (emptyText) {
      return (
        <div
          className={`flex items-center gap-2 text-sm text-muted-foreground ${className}`}
        >
          {showIcon && <Filter className="h-4 w-4" />}
          <span>{emptyText}</span>
        </div>
      );
    }
    return null;
  }

  // Conta total de badges (para o botão "Limpar todos")
  const totalBadges = activeFilters.reduce(
    (acc, f) => acc + f.values.length,
    0,
  );

  return (
    <div
      className={`flex flex-wrap items-center gap-2 p-3 bg-muted/30 rounded-lg border border-border ${className}`}
    >
      {showIcon && (
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground mr-1">
          <Filter className="h-4 w-4" />
          <span>Filtros:</span>
        </div>
      )}

      {activeFilters.map((filter) =>
        // Para multi-select, mostra um badge por valor
        filter.values.length > 1 ? (
          <React.Fragment key={filter.dimension}>
            <span className="text-xs text-muted-foreground font-medium">
              {dimensionLabels[filter.dimension]}:
            </span>
            {filter.values.map((val) => (
              <Badge
                key={`${filter.dimension}-${val}`}
                variant="outline"
                className={`flex items-center gap-1.5 px-2.5 py-1 text-xs cursor-pointer transition-colors ${dimensionColors[filter.dimension]}`}
                onClick={() => clearFilterValue(filter.dimension, val)}
              >
                <span className="max-w-[150px] truncate">{val}</span>
                <X className="h-3 w-3 ml-0.5 opacity-70 hover:opacity-100" />
              </Badge>
            ))}
          </React.Fragment>
        ) : (
          <Badge
            key={filter.dimension}
            variant="outline"
            className={`flex items-center gap-1.5 px-2.5 py-1 text-xs cursor-pointer transition-colors ${dimensionColors[filter.dimension]}`}
            onClick={() => clearFilter(filter.dimension)}
          >
            <span className="font-medium">
              {dimensionLabels[filter.dimension]}:
            </span>
            <span className="max-w-[150px] truncate">{filter.values[0]}</span>
            <X className="h-3 w-3 ml-0.5 opacity-70 hover:opacity-100" />
          </Badge>
        ),
      )}

      {totalBadges > 1 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={clearAllFilters}
          className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
        >
          <RefreshCw className="h-3 w-3 mr-1" />
          Limpar todos
        </Button>
      )}
    </div>
  );
}
